import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getPromotions } from '@/lib/api';
import type { Promotion } from '@/lib/types';
import { promotions as fallbackPromotions } from '@/mocks/promotions';

export default function HeroSlider() {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

useEffect(() => {
  const fetchPromotions = async () => {
    try {
      const data = await getPromotions();

      if (data && data.length > 0) {
        setPromotions(data);
      } else {
        setPromotions(
          fallbackPromotions.map((p, i) => ({
            id: p.id,
            title: p.title,
            subtitle: p.subtitle,
            description: p.description,
            badge: p.badge,
            discount: p.discount,
            image: p.image,
            cta: p.cta,
            product_id: p.productId ?? null,
            sort_order: i + 1,
            created_at: "",
            updated_at: "",
          }))
        );
      }
    } catch (error) {
      console.error(error);

      setPromotions(
        fallbackPromotions.map((p, i) => ({
          id: p.id,
          title: p.title,
          subtitle: p.subtitle,
          description: p.description,
          badge: p.badge,
          discount: p.discount,
          image: p.image,
          cta: p.cta,
          product_id: p.productId ?? null,
          sort_order: i + 1,
          created_at: "",
          updated_at: "",
        }))
      );
    }
  };

  fetchPromotions();
}, []);

  const goTo = useCallback(
    (index: number) => {
      if (animating || promotions.length === 0) return;
      setAnimating(true);
      setCurrent(index);
      setTimeout(() => setAnimating(false), 600);
    },
    [animating, promotions.length]
  );

  const next = useCallback(() => {
    if (promotions.length === 0) return;
    goTo((current + 1) % promotions.length);
  }, [current, goTo, promotions.length]);

  const prev = useCallback(() => {
    if (promotions.length === 0) return;
    goTo((current - 1 + promotions.length) % promotions.length);
  }, [current, goTo, promotions.length]);

  useEffect(() => {
    if (promotions.length === 0) return;
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next, promotions.length]);

  if (promotions.length === 0) {
    return (
      <section className="relative w-full h-[85vh] min-h-[600px] bg-[#f8f4ff] flex items-center justify-center">
        <i className="ri-loader-4-line text-4xl animate-spin text-[#00b4c8]"></i>
      </section>
    );
  }

  const slide = promotions[current];

  return (
    <section className="relative w-full h-[85vh] min-h-[600px] overflow-hidden">
      {promotions.map((promo, i) => (
        <div
          key={promo.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === current ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={promo.image}
            alt={promo.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/30 to-transparent"></div>
        </div>
      ))}

      <div className="relative z-10 h-full flex items-center">
        <div className="max-w-7xl mx-auto px-4 md:px-8 w-full">
          <div
            key={current}
            className="max-w-xl"
            style={{ animation: 'fadeInUp 0.6s ease forwards' }}
          >
            <span className="inline-block border border-[#7ee8f4] text-[#7ee8f4] text-xs font-semibold px-4 py-1.5 rounded-full mb-4 tracking-widest uppercase">
              {slide.badge}
            </span>
            <h1
              className="text-4xl md:text-6xl font-bold text-white leading-tight mb-3"
            >
              {slide.title}
            </h1>
            <p className="text-lg text-white/80 mb-2 font-medium">{slide.subtitle}</p>
            <p className="text-base text-white/70 mb-8 leading-relaxed">{slide.description}</p>
            <div className="flex flex-wrap gap-4">
              <Link
                to={`/menu?producto=${slide.product_id}`}
                className="bg-[#00b4c8] text-white font-semibold px-8 py-3.5 rounded-full hover:bg-[#0099aa] transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                {slide.cta}
              </Link>
              <Link
                to="/menu"
                className="border-2 border-[#f9b8c8] text-[#f9b8c8] font-semibold px-8 py-3.5 rounded-full hover:bg-[#f9b8c8]/10 transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                Explorar Menú
              </Link>
            </div>
            {slide.discount && (
              <div className="mt-6 inline-flex items-center gap-2 bg-[#fde68a]/20 border border-[#fde68a]/50 rounded-full px-4 py-2">
                <i className="ri-price-tag-3-line text-[#fde68a]"></i>
                <span className="text-[#fde68a] font-bold text-sm">{slide.discount}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <button
        onClick={prev}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 text-white transition-colors cursor-pointer backdrop-blur-sm"
        aria-label="Anterior"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 text-white transition-colors cursor-pointer backdrop-blur-sm"
        aria-label="Siguiente"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {promotions.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`transition-all duration-300 rounded-full cursor-pointer ${
              i === current ? 'w-8 h-2.5 bg-[#7ee8f4]' : 'w-2.5 h-2.5 bg-white/50 hover:bg-white/80'
            }`}
            aria-label={`Ir a slide ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
}
